

























import { namespace } from 'vuex-class';
import { Vue, Component, Provide } from 'vue-property-decorator';

import AppFooter from '@/components/footer/AppFooter.vue';
import AppWebHeader from '@/components/nav/AppWebHeader.vue';
import StyleInjector from '@/components/StyleInjector.vue';

const Experiments = () => import(/* webpackChunkName: "experiments" */ '@/components/experiments/Experiments.vue');
const LowPriorityComponents = () =>
	import(/* webpackChunkName: "low-priority-components" */ '@/components/LowPriorityComponents.vue');
const LoginFlow = () => import(/* webpackChunkName: "login-flow" */ '@/components/login/LoginFlow.vue');

import { CollectionType } from '@/enums/collection-type';
import { WebLocale } from '@/enums/web-locale';

import StateHelper from '@/helpers/state-helper';

import Genre from '@/interfaces/genre';
import Provider from '@/interfaces/provider';

import type { FilterCollection } from '@/stores/filter.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Meta = namespace('meta');
const Routing = namespace('routing');
const SearchSuggester = namespace('searchSuggester');
const User = namespace('user');

// @ts-ignore
import LazyHydrate from 'vue-lazy-hydration';
// @ts-ignore
import NoSSR from 'vue-no-ssr';

// make NoSSR a globaly available component
Vue.component('NoSSR', NoSSR);
Vue.component('LazyHydrate', LazyHydrate);

// AppFooter global to avoid CLS
Vue.component('AppFooter', AppFooter);

// SSR Content
import ContentSSR from '@/components/ContentSSR.vue';
Vue.component('Content', ContentSSR);

@Component({
	name: 'SSRApp',
	components: {
		AppWebHeader,
		Experiments,
		LowPriorityComponents,
		StyleInjector,
		LoginFlow,
	},
	metaInfo() {
		return this.head;
	},
})
export default class App extends Vue {
	@Constant.Getter genresByShortName: Record<string, Genre>;
	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Language.Getter locale: string;
	@Language.State webLocale: WebLocale;

	@Meta.Getter head: any;

	@Routing.Getter lastActiveCollectionType: null | CollectionType;

	@SearchSuggester.Getter('isShown') searchSuggesterIsShown: boolean;

	@User.Getter isLoggedIn: () => boolean;

	@Provide('disableScroll')
	disableScroll() {
		if (process.client) {
			document.body.style.overflow = 'hidden';
		}
	}

	@Provide('enableScroll')
	enableScroll() {
		if (process.client) {
			document.body.style.overflow = 'auto';
		}
	}

	@Provide('lowPriorityCallback')
	lowPriorityCallback(cb: Function) {
		if ('requestIdleCallback' in window) {
			window.requestIdleCallback(() => cb());
		} else {
			this.$nextTick(() => cb());
		}
	}

	/**
	 * with these parameters we can always link to the correct url for each collectionType page.
	 */
	@Provide('routeParams')
	routeParams(collectionType: CollectionType): any {
		const isNewPageCollectionType = [
			CollectionType.COMING_SOON,
			CollectionType.LEAVING_SOON,
			CollectionType.PRICEDROPS,
		].includes(collectionType);

		const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
		const filters = this.filters(filterCollectionType);
		const routeName = StateHelper.getRouteName(this.webLocale, collectionType, filters);
		const params = StateHelper.getUrlParamsFromFilterState(
			this.webLocale,
			routeName,
			filters,
			this.providersByShortName,
			this.genresByShortName,
			collectionType,
			this.$route.query
		);
		return {
			name: routeName,
			...params,
		};
	}

	async mounted() {
		const { initStoreWatchers } = await import(/* webpackChunkName: "main.common" */ '@/main.common');
		initStoreWatchers(this.$context);

		this.initPerformanceMonitor();

		const { initCoreWebVitalsMonitor } = await import('@/helpers/tracking/tracking-helper');
		initCoreWebVitalsMonitor();

		if (this.isLoggedIn()) {
			const IapHelper = await (await import('@/helpers/iap-helper')).default;
			await IapHelper.updatePremiumStatus();
		}

		Vue.nextTick(() => {
			const int = setInterval(() => {
				// remove loader
				const loader = document.querySelector('#loader');
				loader?.parentNode?.removeChild(loader);
				clearInterval(int);
			}, 50);

			// reactivate scroll on page rendered
			this.enableScroll();
		});
	}

	async initPerformanceMonitor() {
		const { TrackingHelper } = await import('@/helpers/tracking/tracking-helper');
		const { SnowplowPerformanceContext } = await import('@/helpers/tracking/providers');
		const { PerformanceMonitor } = await import('@/helpers/tracking/performance/PerformanceMonitor');

		let performanceMonitor: any;

		try {
			performanceMonitor = new PerformanceMonitor();
		} catch (error) {
			console.error(error);
		}

		performanceMonitor.onInitialMetricsCollected().then(async () => {
			await TrackingHelper.trackEvent('performance_metrics', { action: 'initial', nonInteraction: true }, [
				new SnowplowPerformanceContext(
					performanceMonitor.performanceMetrics,
					performanceMonitor.getNavigationEntry()
				),
			]);
		});

		performanceMonitor.onFinalMetricsCollected().then(async () => {
			await TrackingHelper.trackEvent('performance_metrics', { action: 'loaded', nonInteraction: true }, [
				new SnowplowPerformanceContext(
					performanceMonitor.performanceMetrics,
					performanceMonitor.getNavigationEntry()
				),
			]);
		});
	}
}
